import { useContext } from 'react';
import { useTheme } from '@emotion/react';
import { useQual, useStore, usePhoneNumber } from 'hooks';
import { MicrositeContext, OffersContext } from 'context';
import { generateReservationTokenPayload } from 'services/moving-app/generateReservationToken';
import { SiteMode } from 'types';
import { PageRoutes } from 'constants/pageRoutes';
import { useShallow } from 'zustand/react/shallow';
import { useTracking } from 'react-tracking';
import { useMicrositeMovingAppBuyflowUrl } from './useProviderUrls';
import { buildThemeWithAssets } from 'services/moving-app/buildThemeAssets';

export function useAsyncCheckoutUrl(): string | undefined {
  const { name: source, gtmCode, iFrameConfig } = useContext(MicrositeContext);
  const theme = useTheme();
  const qual = useQual();
  const supportPhoneNumber = usePhoneNumber();
  const {
    uuid,
    userFirstName,
    userLastName,
    userEmail,
    userPhoneNumber,
    userMoveDate,
  } = qual;
  // Rollout shim (COREX-1867): consumer-app now reads the raw option slots off
  // `qualification.optionalFields`, which the token already carries. This keeps gclid
  // reaching consumer-app builds deployed before that change. Safe to delete once
  // consumer-app is out everywhere.
  const optionalFieldsById = qual.optionalFields?.option5
    ? { gclid: qual.optionalFields.option5 }
    : {};
  const movingAppBuyflowUrl = useMicrositeMovingAppBuyflowUrl();
  const offersContext = useContext(OffersContext);
  const { primaryData, secondaryData } = offersContext;
  const [asyncChosenOffer] = useStore(
    useShallow((store) => [store.asyncChosenOffer])
  );
  const [siteMode] = useStore((store) => [store.siteMode]);
  const requestId =
    siteMode === SiteMode.DEFAULT_MODE
      ? primaryData.requestId
      : secondaryData.requestId;
  const availableProviders =
    siteMode === SiteMode.DEFAULT_MODE
      ? primaryData.availableProviders
      : secondaryData.availableProviders;
  const { trackEvent } = useTracking({
    object: 'async-checkout-url',
  });
  try {
    if (asyncChosenOffer === undefined) {
      throw new Error('asyncChosenOffer undefined');
    }
    if (!qual) {
      throw new Error('no qual');
    }
    if (!uuid) {
      throw new Error('no uuid');
    }

    if (!qual.gtmCode && gtmCode) qual.gtmCode = gtmCode;

    if (!movingAppBuyflowUrl) {
      console.warn(
        `[useAsyncCheckoutUrl] movingAppBuyflowUrls not configured for microsite: ${source}, falling back to lead mode`
      );
      return PageRoutes.leadModeCheckout;
    }

    // eslint-disable-next-line no-console
    console.log('[useAsyncCheckoutUrl] asyncChosenOffer', asyncChosenOffer);

    const provider = availableProviders?.find(
      (p) => p.code === asyncChosenOffer?.providerCode
    );
    const providerInfo = {
      id: provider?.id || '',
      code: asyncChosenOffer?.providerCode || '',
      logoUrl: provider?.logo?.url || '',
    };

    // Build theme with assets for moving app
    const themeWithAssets = buildThemeWithAssets({
      customTheme: theme.customTheme,
      includeAssets: !iFrameConfig?.hideHeaderAndFooter,
    });

    const payload = generateReservationTokenPayload({
      supportPhoneNumber,
      source,
      themeWithAssets,
      customerInfo: {
        firstName: userFirstName,
        lastName: userLastName,
        email: userEmail,
        phoneNumber: userPhoneNumber,
        moveDate: userMoveDate,
      },
      qual,
      chosenOffer: asyncChosenOffer,
      optionalFieldsById,
      uuid,
      requestId,
      providerInfo,
    });
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
    // If encoded qual is > 15000 chars, the URL and/or Request Headers will be
    // too large to behave reliably across browsers and cloudflare.
    if (encoded.length > 15000) {
      const payloadTooLargeMessage = `Encoded payload is too large: ${encoded.length} chars (max: 15000)`;
      console.warn(`[useAsyncCheckoutUrl] ${payloadTooLargeMessage}`);
      trackEvent({
        verb: 'errored',
        details: {
          message: payloadTooLargeMessage,
          asyncChosenOffer,
          qual,
          uuid,
        },
      });
      // Direct such offers to lead mode
      return PageRoutes.leadModeCheckout;
    }

    const url = `${movingAppBuyflowUrl}?uuid=${uuid}&reservation=${encodeURIComponent(
      encoded
    )}`;
    // eslint-disable-next-line no-console
    console.log(url);
    // We're sending users to a different app here; we can't use next router.
    return url;
  } catch (err) {
    console.log(`[useAsyncCheckoutUrl] ${err.message}`);
  }
  return undefined;
}
